// Library
import store from "@/store";
import { err, ok, Result } from "neverthrow";
// Services
import { IIpcService, IpcService } from "@/framework/services/general/ipcService";
import { AuthenticationRepository, IAuthenticationRepository } from "@/framework/repositories/authenticationRepository";
import { StaffGroup } from "@/framework/types/staffGroup";

export interface IpcSendEvent {
  channel: string;
  message: string;
}

export interface IElectronService {
  /**
   * Informs the client about the login and sends the login information to the electron app.
   * @returns Result with `null` for success or an `Error`.
   */
  sendLoginEvent(): Promise<Result<IpcSendEvent, Error>>;

  /**
   * Informs the client about the logout and the `log-out` event to the electron app.
   */
  sendLogoutEvent(): Promise<Result<IpcSendEvent, Error>>;

  /**
   * Sends the client the updated staff data to the electron app.
   * @param staffGroups Staff `belongs` property in `fe-pwa` and `fe-staff`.
   * @returns Result with `IpcSendEvent` for success or an `Error`.
   */
  sendUpdateStaffGroupEvent(staffGroups: StaffGroup[]): Result<IpcSendEvent, Error>;
}

/**
 * Service to handle the communication to the electron desktop app.
 */
export class ElectronService implements IElectronService {
  authenticationRepository: IAuthenticationRepository;
  ipcService: IIpcService;

  constructor(
    authenticationRepository: IAuthenticationRepository = new AuthenticationRepository(),
    ipcService: IIpcService = new IpcService()
  ) {
    this.authenticationRepository = authenticationRepository;
    this.ipcService = ipcService;
  }

  /**
   * Informs the client about the login and sends the login information to the electron app.
   * @returns Result with `IpcSendEvent` for success or an `Error`.
   */
  sendLoginEvent = async (): Promise<Result<IpcSendEvent, Error>> => {
    let staff = null;
    if (store.getters.staff && store.getters.staff.myself) {
      staff = store.getters.staff.myself; // InRoom
    }
    let tenantId = null;
    if (store.getters.tenantId) {
      tenantId = store.getters.tenantId; // InRoom
    }
    let accessId = staff && staff.access_id ? staff.access_id : null;

    let role = null;
    if (staff && staff.role) {
      role = staff && staff.role; // InRoom
    }
    let belongs = null;
    if (staff && staff.belongs) {
      belongs = staff.belongs; // InRoom
    }
    const ktznAuthTokenRes = await this.authenticationRepository.getKtznAuthToken();
    if (ktznAuthTokenRes.isOk() && tenantId && accessId && role) {
      const channel = "log-in";
      const data = {
        ktznAuthAccessToken: ktznAuthTokenRes.value.accessToken,
        ktznAuthRefreshToken: ktznAuthTokenRes.value.refreshToken,
        userAgent: navigator.userAgent,
        accessId: accessId,
        role: role,
        tenantId: tenantId,
        staffGroups: belongs,
        webSocketUrl: store.getters.getSubscriptionEndpoint,
      };
      console.log("ipc - sendLoginEvent", data);
      const dataString = JSON.stringify(data);
      this.ipcService.send(channel, dataString);
      return ok({ channel: channel, message: dataString } as IpcSendEvent);
    } else {
      return err(new Error("Error when trying to send 'log-in' to electron"));
    }
  };

  /**
   * Informs the client about the logout and the `log-out` event to the electron app.
   */
  sendLogoutEvent = async (): Promise<Result<IpcSendEvent, Error>> => {
    try {
      const channel = "log-out";
      const data = "";
      this.ipcService.send(channel, data);
      return ok({ channel: channel, message: data } as IpcSendEvent);
    } catch (error) {
      return err(new Error("Error when trying to send 'log-out' to electron"));
    }
  };

  /**
   * Sends the client the updated staff data to the electron app.
   * @param staffGroups Staff `belongs` property in `fe-pwa` and `fe-staff`.
   * @returns Result with `IpcSendEvent` for success or an `Error`.
   */
  sendUpdateStaffGroupEvent = (staffGroups: StaffGroup[]): Result<IpcSendEvent, Error> => {
    try {
      const channel = "update-staff-group";
      const data = JSON.stringify(staffGroups);
      this.ipcService.send(channel, data);
      return ok({ channel: channel, message: data } as IpcSendEvent);
    } catch (error) {
      return err(new Error("Error when trying to send 'log-out' to electron"));
    }
  };
}
